import { Head, router, useForm, usePage } from '@inertiajs/react';
import { useState } from 'react';

import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { trackUmamiEvent } from '@/lib/analytics';
import { inputClass } from '@/lib/formStyles';

interface Account {
  id: number;
  email: string;
  createdAt: string;
  isCurrent: boolean;
}

interface AccountsPageProps {
  accounts: Account[];
  [key: string]: unknown;
}

const labelClass = 'text-on-surface mb-1.5 block text-sm font-medium';
const dateFormatter = new Intl.DateTimeFormat('pt-PT', { dateStyle: 'short' });

function formatDate(isoString: string | null): string {
  if (!isoString) return '—';
  return dateFormatter.format(new Date(isoString));
}

// ── Create form ──────────────────────────────────────────────────────────────

function CreateAccountForm() {
  const form = useForm({ email: '', password: '', password_confirmation: '' });
  const [clientError, setClientError] = useState<string | null>(null);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setClientError(null);

    if (form.data.password !== form.data.password_confirmation) {
      setClientError('As palavras-passe não coincidem.');
      return;
    }
    if (form.data.password.length < 12) {
      setClientError('A palavra-passe deve ter pelo menos 12 caracteres.');
      return;
    }

    form.transform((d) => ({ user: { email: d.email, password: d.password } }));
    trackUmamiEvent('admin_account_create');
    form.post('/accounts');
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <div>
          <label className={labelClass} htmlFor="acc-email">
            Email
          </label>
          <input
            id="acc-email"
            type="email"
            value={form.data.email}
            onChange={(e) => form.setData('email', e.target.value)}
            placeholder="email@exemplo.pt"
            autoComplete="off"
            required
            className={inputClass}
          />
          {form.errors.email && <p className="text-error mt-1 text-xs">{form.errors.email}</p>}
        </div>
        <div>
          <label className={labelClass} htmlFor="acc-password">
            Palavra-passe
          </label>
          <input
            id="acc-password"
            type="password"
            value={form.data.password}
            onChange={(e) => form.setData('password', e.target.value)}
            placeholder="mín. 12 caracteres"
            autoComplete="new-password"
            required
            className={inputClass}
          />
          {form.errors.password && (
            <p className="text-error mt-1 text-xs">{form.errors.password}</p>
          )}
        </div>
        <div>
          <label className={labelClass} htmlFor="acc-password-confirm">
            Confirmar
          </label>
          <input
            id="acc-password-confirm"
            type="password"
            value={form.data.password_confirmation}
            onChange={(e) => form.setData('password_confirmation', e.target.value)}
            placeholder="repetir palavra-passe"
            autoComplete="new-password"
            required
            className={inputClass}
          />
        </div>
      </div>
      {clientError && <p className="text-error text-xs">{clientError}</p>}
      <Button
        type="submit"
        variant="primary"
        size="sm"
        disabled={
          form.processing ||
          !form.data.email ||
          !form.data.password ||
          !form.data.password_confirmation
        }
        loading={form.processing}
      >
        {form.processing ? 'A criar...' : 'Criar conta'}
      </Button>
    </form>
  );
}

// ── Change password form (shown in a modal) ───────────────────────────────────

interface ChangePasswordFormProps {
  account: Account;
  onClose: () => void;
}

function ChangePasswordForm({ account, onClose }: ChangePasswordFormProps) {
  const [password, setPassword] = useState('');
  const [clientError, setClientError] = useState<string | null>(null);
  const [processing, setProcessing] = useState(false);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setClientError(null);

    if (password.length < 12) {
      setClientError('A palavra-passe deve ter pelo menos 12 caracteres.');
      return;
    }

    setProcessing(true);
    trackUmamiEvent('admin_account_password_change');
    router.patch(
      `/accounts/${account.id}/password`,
      { user: { password } },
      {
        preserveScroll: true,
        onFinish: () => {
          setProcessing(false);
          onClose();
        },
      },
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <p className="text-on-surface-light text-sm">
        Alterar palavra-passe de{' '}
        <span className="text-on-surface font-medium">{account.email}</span>
      </p>
      <div>
        <label className={labelClass} htmlFor="acc-new-password">
          Nova palavra-passe
        </label>
        <input
          id="acc-new-password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="mín. 12 caracteres"
          autoComplete="new-password"
          required
          className={inputClass}
        />
      </div>
      {clientError && <p className="text-error text-xs">{clientError}</p>}
      <div className="border-line flex justify-end gap-3 border-t pt-4">
        <Button type="button" variant="white" size="sm" onClick={onClose}>
          Cancelar
        </Button>
        <Button
          type="submit"
          variant="primary"
          size="sm"
          disabled={processing || !password}
          loading={processing}
        >
          {processing ? 'A guardar...' : 'Guardar'}
        </Button>
      </div>
    </form>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function Accounts() {
  const { accounts } = usePage<AccountsPageProps>().props;
  const [changingPasswordFor, setChangingPasswordFor] = useState<Account | null>(null);

  function handleDelete(account: Account) {
    if (!window.confirm(`Eliminar a conta "${account.email}"? Esta ação não pode ser desfeita.`))
      return;
    trackUmamiEvent('admin_account_delete', { is_current: account.isCurrent });
    router.delete(`/accounts/${account.id}`);
  }

  return (
    <>
      <Head title="Contas — Administração" />
      <h1 className="font-headline text-on-surface mb-6 text-2xl font-bold">Contas</h1>
      <div className="mx-auto max-w-3xl space-y-6">
        {/* Create form card */}
        <div className="surface-card space-y-4 p-5">
          <h2 className="font-headline text-on-surface text-base font-bold">Nova conta</h2>
          <CreateAccountForm />
        </div>

        {/* Accounts table */}
        <div className="surface-card overflow-hidden">
          {accounts.length === 0 ? (
            <div className="text-on-surface-light flex flex-col items-center gap-2 py-12 text-center text-sm">
              <span className="material-symbols-outlined text-on-surface-light/40 text-3xl">
                manage_accounts
              </span>
              Nenhuma conta encontrada.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[520px] border-collapse text-left">
                <thead>
                  <tr className="border-line border-b">
                    <th className="text-on-surface-light/70 px-4 py-3 text-xs font-bold tracking-wider uppercase">
                      Email
                    </th>
                    <th className="text-on-surface-light/70 px-4 py-3 text-xs font-bold tracking-wider uppercase">
                      Criado em
                    </th>
                    <th className="px-4 py-3" />
                  </tr>
                </thead>
                <tbody>
                  {accounts.map((account) => {
                    const canDelete = !account.isCurrent && accounts.length > 1;
                    return (
                      <tr
                        key={account.id}
                        className="border-line hover:bg-background border-b transition-colors last:border-b-0"
                      >
                        <td className="text-on-surface px-4 py-3.5 text-sm font-semibold">
                          <span className="flex items-center gap-2">
                            <span className="material-symbols-outlined text-on-surface-light/50 text-[16px]">
                              manage_accounts
                            </span>
                            {account.email}
                            {account.isCurrent && (
                              <span className="text-on-surface-light text-xs font-normal">
                                (conta atual)
                              </span>
                            )}
                          </span>
                        </td>
                        <td className="text-on-surface-light px-4 py-3.5 text-xs">
                          {formatDate(account.createdAt)}
                        </td>
                        <td className="px-4 py-3.5">
                          <div className="flex items-center justify-end gap-1">
                            <button
                              type="button"
                              onClick={() => setChangingPasswordFor(account)}
                              aria-label="Alterar palavra-passe"
                              title="Alterar palavra-passe"
                              className="text-on-surface-light hover:bg-primary/10 hover:text-primary rounded-md p-1.5 transition-colors"
                            >
                              <span className="material-symbols-outlined text-[18px]">key</span>
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDelete(account)}
                              aria-label="Eliminar conta"
                              disabled={!canDelete}
                              title={
                                account.isCurrent
                                  ? 'Não pode eliminar a sua própria conta'
                                  : accounts.length <= 1
                                    ? 'Não pode eliminar a última conta'
                                    : 'Eliminar conta'
                              }
                              className="text-on-surface-light hover:bg-error/10 hover:text-error disabled:hover:text-on-surface-light rounded-md p-1.5 transition-colors disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-transparent"
                            >
                              <span className="material-symbols-outlined text-[18px]">delete</span>
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Change password modal */}
      <Modal
        open={Boolean(changingPasswordFor)}
        onClose={() => setChangingPasswordFor(null)}
        title="Alterar palavra-passe"
      >
        {changingPasswordFor && (
          <ChangePasswordForm
            account={changingPasswordFor}
            onClose={() => setChangingPasswordFor(null)}
          />
        )}
      </Modal>
    </>
  );
}
